import * as React from 'react';
import { Field } from 'redux-form';
import { forEach, forEachRight, get, isEmpty, map } from 'lodash';
import { II18nMessages } from '../../../../../services/branding/models';
import { IChannel, IProduct, ITraveller, } from '../../../../../data/api/models';
import { Col, Row } from 'react-bootstrap';
import FieldFormControl from '../../../../../components/FieldFormControl';
import * as uuid from 'uuid/v4';
import {
  allowOnlyApostrophes,
  hasEmojis, hasSpecialCharacters, isAdult,
  isChild, isGroupChild,
  isRequired,
  validateDays,
  validateMonths,
  validateYear,
} from '../../../../../util/validators';
import { TITLES } from '../../../../../helpers/Quote';
import { ErrorDisplay } from '../../../../../components/FieldFormControl/ErrorDisplay';

type TravellerType = 'child' | 'adult';

interface ITravellerTypeConfig {
  min: number;
  max: number;
}

interface IGroupType {
  adult: ITravellerTypeConfig;
  child?: ITravellerTypeConfig;
}

interface IGroupTypeMap {
  [type: string]: IGroupType;
}

interface ITripDetailsForm {
  i18n: II18nMessages;
  pristine: boolean;
  fields: any;
  travellers: any[];
  groupType: string;
  channel: IChannel;
  change: any;
  selectedAdultNumber: { value: number, label: number };
  selectedChildrenNumber: { value: number, label: number };
  synchronousError: any;
  disableButtons?: (disable: boolean) => any;

  renderDefinition(name: string): any;

  product: IProduct;
  isRenewal: boolean;
}

const groupTypeTravellerMap: IGroupTypeMap = {
  individual: {
    adult: {
      min: 1,
      max: 1,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  couple: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  group: {
    adult: {
      min: 1,
      max: 10,
    },
    child: {
      min: 0,
      max: 9,
    },
  },
};

const defaultTraveller = {
  id: '',
  title: '',
  firstName: '',
  lastName: '',
  dob: '',
  phone: '',
  email: '',
  type: '',
  screening: null,
  screeningSessionId: null,
  claims: undefined,
  criminal: undefined,
};

export class Travellers extends React.Component<ITripDetailsForm> {
  private getAll = (type: TravellerType, fields) => fields.getAll().filter((traveller) => traveller.type === type);
  private count = (type: TravellerType, fields) => this.getAll(type, fields).length;

  constructor(props) {
    super(props);
    const { fields, groupType } = this.props;
    const config = this.getConfig(groupType);
    if (config) {
      const { adult, child } = config;

      this.handleTravellerUpdates('adult', adult, fields);
      this.handleTravellerUpdates('child', child, fields);
    }
  }

  private handleTravellerUpdates = (type: TravellerType, config: ITravellerTypeConfig, fields) => {
    let total = this.count(type, fields);

    if (total > config.max) {
      // Remove travellers from the form until the maximum amount of travellers are
      // achieved for the next group type.

      forEachRight(this.getAll(type, fields), (traveller) => {
        if (total <= config.max) {
          return;
        }

        this.removeField(traveller.id, fields);
        total -= 1;
      });
    }

    if (total < config.min) {
      const totalToAdd = config.min - total;

      for (let i = 0; i < totalToAdd; i++) {
        this.addField(type, fields);
      }
    }
  }

  public componentDidUpdate(prevProps: Readonly<ITripDetailsForm>, prevState: Readonly<{}>, snapshot?: any) {
    const { fields, groupType, travellers } = this.props;
    const config = this.getConfig(groupType);
    if (config) {
      const { adult, child } = config;

      this.handleTravellerUpdates('adult', adult, fields);
      this.handleTravellerUpdates('child', child, fields);
    }

    if (prevProps.groupType === groupType) {
      forEach(travellers, (value: ITraveller, index) => {
        if (get(value, 'dobSeparate') && index > 0) {
          this.changeDOBField(value, index);
        }
      })
    }

  }

  private changeDOBField = (value, index) => {
    const { change } = this.props;
    change(`travellers[${index}].dob`, (get(value, 'dobSeparate.day', '') + '/' + get(value, 'dobSeparate.month', '') + '/' + get(value, 'dobSeparate.year', '')))
  }

  private addField = (type: TravellerType, fields) => {
    fields.push({
      ...defaultTraveller,
      id: uuid(),
      type,
    });
  }

  private getConfig = (groupType) => {
    const {
      selectedAdultNumber,
      selectedChildrenNumber,
    } = this.props
    let config;
    if (groupType === 'family' || groupType === 'family_one_adult' || groupType === 'group') {
      config = {
        adult: { min: selectedAdultNumber.value, max: selectedAdultNumber.value },
        child: { min: selectedChildrenNumber.value, max: selectedChildrenNumber.value }
      }
    } else {
      config = groupTypeTravellerMap[groupType ? groupType : ''];
    }

    return config;
  }

  private getIndex = (id: string, fields) => {
    let travellerIndex = -1;
    const travellers = fields.getAll();

    travellers.forEach((traveller, index) => {
      if (traveller.id === id) {
        travellerIndex = index;
      }
    });

    if (travellerIndex === -1) {
      console.error('Couldn\'t find traveller', travellers, id, travellerIndex);
    }

    return travellerIndex;
  }

  private removeField = (id: string, fields) => {
    const travellerIndex = this.getIndex(id, fields);

    if (travellerIndex === -1) {
      return;
    }

    fields.remove(travellerIndex);
  }

  private renderFields(type: TravellerType) {
    const { fields, travellers, disableButtons, groupType } = this.props;

    const allTypeFields = this.getAll(type, fields);

    if (isEmpty(allTypeFields)) {
      return null;
    }

    return allTypeFields.map((traveller, index) => {
      const fieldIndex = this.getIndex(traveller.id, fields);
      const selectedTitle = travellers[fieldIndex].title;
      const isTravellerChild = type === 'child';
      const day = get(traveller, 'dobSeparate.day');
      const month = get(traveller, 'dobSeparate.month');
      const year = get(traveller, 'dobSeparate.year');
      const isGroup = groupType === 'group';
      let dobError = null;
      disableButtons(false)
      if (index === 0 && type === 'adult') {
        return null;
      }

      if (day && month && year) {
        dobError = isTravellerChild ? isGroup ? isGroupChild(`${day}/${month}/${year}`) : isChild(`${day}/${month}/${year}`) : isAdult(`${day}/${month}/${year}`)
        if (dobError) {
          disableButtons(true)
        } else {
          disableButtons(false)
        }
      }

      return (
        <div key={index}>
          <div>
            <div>
              <h3>{type === 'adult' ? 'Adult ' + (index + 1) : 'Child ' + (index + 1)}</h3>
            </div>
            <Row>
              <Col xs={6} className={'full-width-xs'}>Their preferred title</Col>
              <Col xs={6} className={'full-width-xs title-selection-container'}>
                {map(TITLES, (value, key) => {
                  const isSelected = selectedTitle === value;
                  return (
                    <Col xs={4}
                         className={isSelected ? 'traveller-title-selection selected' : 'traveller-title-selection'}
                         key={key}>
                      <div>
                        <Field
                          name={`travellers[${fieldIndex}].title`}
                          id={key + '-btn'}
                          type="radio"
                          value={value}
                          component={FieldFormControl}
                          validate={[isRequired]}
                        >
                          <div className={`trip-type-btn ${key}`}>
                            <div className={'trip-type-selection-container'}>
                              {value}
                            </div>
                          </div>
                        </Field>
                      </div>
                    </Col>
                  )
                })}
              </Col>
            </Row>
            <Row className={'adm-form-group'}>
              <Col xs={6} className={'full-width-xs'}>First Name</Col>
              <Col xs={6} className={'full-width-xs'}>
                <Field
                  name={`travellers[${fieldIndex}].firstName`}
                  type="text"
                  component={FieldFormControl}
                  validate={[isRequired, hasEmojis, hasSpecialCharacters]}
                  autocomplete="new-password"
                />
              </Col>
            </Row>
            <Row className={'adm-form-group'}>
              <Col xs={6} className={'full-width-xs'}>Last Name</Col>
              <Col xs={6} className={'full-width-xs'}>
                <Field
                  name={`travellers[${fieldIndex}].lastName`}
                  type="text"
                  component={FieldFormControl}
                  validate={[isRequired, hasEmojis, allowOnlyApostrophes]}
                  autocomplete="new-password"
                />
              </Col>
            </Row>
            <Row className={'adm-form-group'}>
              <Col xs={6} className={'full-width-xs'}>Date of Birth</Col>
              <Col xs={2} className={'full-width-xs'}>
                <Field
                  name={`travellers[${fieldIndex}].dobSeparate.day`}
                  inputmode="numeric"
                  type="text"
                  placeholder={'DD'}
                  className={'text-center'}
                  maxLength={'2'}
                  component={FieldFormControl}
                  validate={type === 'adult' ? [
                    isRequired,
                    validateDays,
                  ] : [
                    isRequired,
                    validateDays,
                  ]}
                  autocomplete="new-password"
                />
                <div className={'text-center'}>Day</div>
              </Col>
              <Col xs={2} className={'full-width-xs'}>
                <Field
                  name={`travellers[${fieldIndex}].dobSeparate.month`}
                  inputmode="numeric"
                  type="text"
                  maxLength={'2'}
                  placeholder={'MM'}
                  className={'text-center'}
                  component={FieldFormControl}
                  validate={type === 'adult' ? [
                    isRequired,
                    validateMonths,
                  ] : [
                    isRequired,
                    validateMonths,
                  ]}
                  autocomplete="new-password"
                />
                <div className={'text-center'}>Month</div>
              </Col>
              <Col xs={2} className={'full-width-xs'}>
                <Field
                  name={`travellers[${fieldIndex}].dobSeparate.year`}
                  inputmode="numeric"
                  type="text"
                  maxLength={'4'}
                  placeholder={'YYYY'}
                  className={'text-center'}
                  component={FieldFormControl}
                  validate={type === 'adult' ? [
                    isRequired,
                    validateYear,
                  ] : [
                    isRequired,
                    validateYear
                  ]}
                  autocomplete="new-password"
                />
                <div className={'text-center'}>Year</div>
              </Col>
              <Col xs={6}/>
              <Col xs={6} className={'full-width-xs'}>
                <ErrorDisplay error={dobError}/>
              </Col>
            </Row>
          </div>
        </div>
      );
    });
  }

  public render() {
    const { groupType } = this.props;
    const config = this.getConfig(groupType);

    return (
      <div>
        <div className="">
          <div className="">
            <div className="margin-bottom">
              {this.renderFields('adult')}
            </div>
          </div>
          <div className={get(config, 'child.max', 0) === 0 ? 'hidden' : ''}>
            <div className="margin-bottom">
              {this.renderFields('child')}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
